import React from 'react';
import ReactTooltip from 'react-tooltip';
import {
	Loader,
	BaasicTable,
	TableFilter,
	BaasicConfirmModal,
	BaasicCheckbox,
	BaasicHeader,
	BaasicTableExport,
	NotifyOutsideClick,
	BaasicDropdown,
} from 'core/components';
import { observer } from 'mobx-react';
import { defaultTemplate } from 'core/hoc';
import { fileNames } from 'common/constants';
import { ExportToCSVByDate, FulfillmentOrderItemsExpandList } from 'modules/order/pages';
import OrderDateRangeFilter from 'modules/order/pages/OrderDateRangeFilter';
import _ from 'lodash';

const OrderListTemplate = ({
	t,
	viewStore,
	viewStore: {
		loading,
		listStore,
		tableData,
		baasicTableSettingsViewStore,
		queryUtility,
		columnFilterSetup,
		removeFiltersModal,
		pdfURL,
		pdfURLReturn,
		isSeller,
		isPickingMode,
		findFulfillmentOrders,
		isSuperAdminOrganization,
		onSelectAllCheckbox,
		showSelectAllColumn,
		onSelectFulfillmentOrderCheckbox,
		group,
		expandChange,
		groupable,
		change,
		groupByDropdownStore,
		warehouseDropdownStore,
	},
}) => {
	if (loading) return <Loader />;
	return (
		<section className="layout__main__body">
			<BaasicHeader
				title="MAINMENU.FULFILLMENTORDERS.TITLE"
				baasicTableSettingsViewStore={baasicTableSettingsViewStore}
				reloadFunc={findFulfillmentOrders}
				disabledBtn={
					isSeller || isSuperAdminOrganization ? isPickingMode && warehouseDropdownStore.value == null : false
				}
			>
				<HeaderComponents {...{ viewStore, t }} />
				<BaasicTableExport tableViewStore={viewStore} fileTitle={fileNames.fulfillmentOrder} />
			</BaasicHeader>
			<div className={`k-grid--bulk ${groupByDropdownStore.value ? 'group' : ''}`}>
				<BaasicTable
					{...{
						tableData,
						queryUtility,
						viewStore,
						detailComponent: FulfillmentOrderItemsExpandList,
						groupable,
						group,
						expandChange,
						change,
					}}
					rowRender={CustomRowRender} // Production mode: Row has red background. Is multi item: Expand button is visible, else hidden.
					cellRender={CellRender}
					data={listStore.data.slice()}
					actions={viewStore}
					onSelectAllHeaderClick={onSelectAllCheckbox}
					showSelectAllColumn={showSelectAllColumn}
					onCheckboxChange={onSelectFulfillmentOrderCheckbox}
				/>
			</div>
			<TableFilter viewStore={viewStore} columnFilterSetup={columnFilterSetup} />
			<BaasicConfirmModal modalParams={removeFiltersModal.removeFiltersConfirm} />
			<ReactTooltip />
			<iframe id="pdf-print" name="pdf-print" src={pdfURL} style={{ display: 'none' }}></iframe>
			<iframe id="pdf-print-return" name="pdf-print-return" src={pdfURLReturn} style={{ display: 'none' }}></iframe>
		</section>
	);
};

const HeaderComponents = observer(({ viewStore, t }) => {
	const {
		useRegularPalletizingMode,
		onWarehouseSettingCheck,
		onCheck,
		showTodayShipments,
		isSeller,
		isSuperAdminOrganization,
		isPickingMode,
		orderDateRangeViewStore,
		groupByDropdownStore,
		warehouseDropdownStore,
	} = viewStore;
	return (
		<div className="flex--primary flex--align--middle align--h--right">
			{!showTodayShipments && <OrderDateRangeFilter orderDateRangeViewStore={orderDateRangeViewStore} />}
			<BaasicDropdown
				dropdownStore={groupByDropdownStore}
				className="w--200--px spc--right--med"
				isClearable={true}
				placeholder={t('ORDERVIEW.PLACEHOLDER.GROUPBYSELECT')}
			/>
			{(isSeller || isSuperAdminOrganization) && isPickingMode && (
				<BaasicDropdown
					dropdownStore={warehouseDropdownStore}
					placeholder={t('ORDERVIEW.PLACEHOLDER.WAREHOUSESELECT')}
					className="w--200--px spc--right--med"
				/>
			)}
			<BulkActions {...{ viewStore, t }} />
			{!isSeller && !isSuperAdminOrganization && (
				<div className="layout__main__subheader__item">
					<BaasicCheckbox
						id={'useRegularPalletizingMode'}
						onChangeCallback={onWarehouseSettingCheck}
						label="ORDERVIEW.USEREGULARPALLETIZINGMODE"
						checked={useRegularPalletizingMode}
						className="input--check input--check--negative"
					/>
				</div>
			)}
			<div className="layout__main__subheader__item">
				<BaasicCheckbox
					id={'showTodayShipments'}
					onChangeCallback={onCheck}
					label="ORDERVIEW.SHOWTODAYSHIPMENTS"
					checked={showTodayShipments}
					className="input--check input--check--negative"
				/>
			</div>
			<ExportToCSVByDate />
		</div>
	);
});

const BulkActions = observer(
	({
		viewStore: {
			isPickingMode,
			isSeller,
			isSuperAdminOrganization,
			isBulkPrintMode,
			selectedItems,
			closePopup,
			toggleBulkMenu,
			isBulkActionPopupOpen,
			bulkActionsArray,
		},
		t,
	}) => {
		if (!isPickingMode && (isSeller || isSuperAdminOrganization) && !isBulkPrintMode && selectedItems.length > 0) {
			return (
				<div className="header__left--mode">
					<NotifyOutsideClick action={closePopup}>
						<div className="header__profile" onClick={toggleBulkMenu}>
							<div>
								<span className="spc--right--tny">{t('ORDERVIEW.PLACEHOLDER.BULKACTIONSELECT')}</span>
								<i className="fa fa-angle-down spc--left--med" />
							</div>
							<div className={'header__profile--dropdown' + (isBulkActionPopupOpen ? ' active' : '')}>
								<ul>
									{_.map(bulkActionsArray, action => {
										if (action.display) {
											return (
												<li key={action.id} className="header__profile--dropdown__item" onClick={action.onClick}>
													{t(action.name)}
												</li>
											);
										}
									})}
								</ul>
							</div>
						</div>
					</NotifyOutsideClick>
				</div>
			);
		}
	}
);

function CustomRowRender(trElement, { dataItem }) {
	const isMultiItem = _.has(dataItem, 'orderedItemCount') ? dataItem.orderedItemCount > 1 : null;
	let className = '';
	if (isMultiItem) {
		className = dataItem.productionMode ? trElement.props.className + ' is-warning' : trElement.props.className;
	} else {
		className = dataItem.productionMode
			? 'not-expandable ' + trElement.props.className + ' is-warning'
			: 'not-expandable ' + trElement.props.className;
	}
	return <tr className={className}>{trElement.props.children}</tr>;
}

function CellRender(td, props) {
	if (td && td.props.children && props.rowType === 'groupHeader' && props.dataItem.showSelectCheckbox) {
		let children = <GroupCheckbox dataItem={props.dataItem} td={td} />;
		return React.cloneElement(td, td.props, children);
	}
	return td;
}

const GroupCheckbox = observer(({ dataItem, td }) => {
	return (
		<div className="flex--primary">
			<div className="spc--right--sml">
				<BaasicCheckbox
					id={dataItem.value}
					checked={dataItem.isChecked}
					onChangeCallback={() => dataItem.onSelectGroupCheckbox(dataItem)}
					className={`input--check ${dataItem.isPartiallyChecked ? 'input--check--partially' : ''}`}
				/>
			</div>
			{td.props.children}
		</div>
	);
});

export default defaultTemplate(OrderListTemplate);



// WEBPACK FOOTER //
// ./src/themes/default/modules/order/pages/OrderListTemplate.jsx